import { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Users, Search, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TeamMember {
  id: string;
  display_name: string;
  email: string;
  auth_user_id: string | null;
}

interface AgentUserAssignmentProps {
  companyId: string | null;
  tenantId?: string | null;
  branchIds?: string[];
  useEnterpriseHierarchy?: boolean;
  assignToAll: boolean;
  onAssignToAllChange: (value: boolean) => void;
  selectedUserIds: string[];
  onSelectedUsersChange: (ids: string[]) => void;
}

export function AgentUserAssignment({
  companyId,
  tenantId = null,
  branchIds = [],
  useEnterpriseHierarchy = false,
  assignToAll,
  onAssignToAllChange,
  selectedUserIds,
  onSelectedUsersChange,
}: AgentUserAssignmentProps) {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const branchScopeKey = branchIds.join(',');

  useEffect(() => {
    async function fetchTeamMembers() {
      if (useEnterpriseHierarchy ? !tenantId : !companyId) {
        setTeamMembers([]);
        return;
      }

      setLoading(true);
      try {
        let query = supabase
          .from('profiles')
          .select('id, display_name, email, auth_user_id')
          .order('display_name');

        query = useEnterpriseHierarchy
          ? query.eq('tenant_id', tenantId)
          : query.eq('company_id', companyId);

        const requestedBranchIds = branchScopeKey ? branchScopeKey.split(',') : [];
        if (useEnterpriseHierarchy && requestedBranchIds.length > 0) {
          query = query.in('company_id', requestedBranchIds);
        }

        const { data, error } = await query;

        if (error) throw error;
        setTeamMembers(data || []);
      } catch (error) {
        console.error('Error fetching team members:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchTeamMembers();
  }, [branchScopeKey, companyId, tenantId, useEnterpriseHierarchy]);

  const filteredMembers = teamMembers.filter(member =>
    member.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    member.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleToggleUser = (userId: string) => {
    if (selectedUserIds.includes(userId)) {
      onSelectedUsersChange(selectedUserIds.filter(id => id !== userId));
    } else {
      onSelectedUsersChange([...selectedUserIds, userId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredMembers.length) {
      onSelectedUsersChange([]);
    } else {
      onSelectedUsersChange(filteredMembers.map(m => m.id));
    }
  };

  if (useEnterpriseHierarchy ? !tenantId : !companyId) {
    return (
      <div className="bg-muted/50 rounded-lg p-4 text-center text-sm text-muted-foreground">
        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p>{useEnterpriseHierarchy ? 'Organisation context is loading' : 'Select a company first to assign to specific users'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle for All vs Specific Users */}
      <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-card">
        <div className="flex items-center gap-3">
          <Users className="h-5 w-5 text-muted-foreground" />
          <div>
            <Label className="text-sm font-medium">Assign to All Team Members</Label>
            <p className="text-xs text-muted-foreground">
              {assignToAll 
                ? 'All team members can access this agent' 
                : 'Only selected members can access this agent'}
            </p>
          </div>
        </div>
        <Switch 
          checked={assignToAll} 
          onCheckedChange={onAssignToAllChange}
        />
      </div>

      {/* User Selection (only shown when not assigning to all) */}
      {!assignToAll && (
        <div className="border border-border rounded-lg">
          <div className="p-3 border-b border-border bg-muted/30">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Select Team Members</span>
              <Badge variant="secondary">
                {selectedUserIds.length} / {teamMembers.length} selected
              </Badge>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          {loading ? (
            <div className="p-4 text-center text-muted-foreground">Loading team members...</div>
          ) : teamMembers.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No team members found for this company
            </div>
          ) : (
            <>
              {/* Select All */}
              <div 
                className="flex items-center gap-3 p-3 border-b border-border hover:bg-muted/50 cursor-pointer"
                onClick={(e) => { e.stopPropagation(); handleSelectAll(); }}
              >
                <input
                  type="checkbox"
                  checked={selectedUserIds.length === filteredMembers.length && filteredMembers.length > 0}
                  onChange={handleSelectAll}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 shrink-0 accent-primary"
                />
                <span className="text-sm font-medium">
                  {selectedUserIds.length === filteredMembers.length ? 'Deselect All' : 'Select All'}
                </span>
              </div>

              {/* User List */}
              <div className="h-[200px] overflow-y-auto">
                {filteredMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer border-b border-border last:border-b-0"
                    onClick={(e) => { e.stopPropagation(); handleToggleUser(member.id); }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(member.id)}
                      onChange={() => handleToggleUser(member.id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 shrink-0 accent-primary"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{member.display_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                    </div>
                    {selectedUserIds.includes(member.id) && (
                      <UserCheck className="h-4 w-4 text-green-500 flex-shrink-0" />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
